






import { Component, Vue, Prop, Watch, Provide } from "vue-property-decorator";
import Vessel from "@/components/base/Vessel.vue";

export interface IFleetData {
  activeIndexes: number[];
  activeVessels: Vue[] | null;
  vessels: Vue[];
}

export interface IFleetMethods {
  registerVessel: (vessel: Vue) => void;
  toggleVessel: (vessel: Vue) => void;
}

@Component
export default class VesselFleet extends Vue {
  // Props

  @Prop({ default: () => [] })
  public readonly value!: number | number[];

  @Prop({ default: false })
  public readonly hasMultiActive!: boolean;

  // Data

  public vessels: IFleetData["vessels"] = [];
  public activeIndexes: IFleetData["activeIndexes"] = this.setActiveIndexes();
  public alwaysActive: number[] = [];

  // Computed

  public get activeVessels(): IFleetData["activeVessels"] {
    return this.vessels?.filter(
      (_, index) =>
        this.activeIndexes.includes(index) || this.alwaysActive.includes(index)
    );
  }

  public get activeIndexesComputed() {
    return [...this.activeIndexes, ...this.alwaysActive];
  }

  // Provide

  @Provide("fleetData")
  public fleetData(): IFleetData {
    return {
      activeIndexes: this.activeIndexesComputed,
      activeVessels: this.activeVessels,
      vessels: this.vessels
    };
  }

  @Provide("fleetMethods")
  public get fleetMethods(): IFleetMethods {
    return {
      registerVessel: this.registerVessel,
      toggleVessel: this.toggleVessel
    };
  }

  // Watchers

  @Watch("value")
  public onValueChange(value): void {
    if (!this.$_.isEqual(value, this.activeIndexes)) {
      this.hasMultiActive
        ? (value as number[]).forEach(i => this.changeVessel(i))
        : this.changeVessel(value);
      this.activeIndexes = this.setActiveIndexes();
    }
  }

  // Methods

  public setActiveIndexes() {
    const newActiveIndexes = this.hasMultiActive
      ? [...(this.value as number[])]
      : [this.value as number];

    return newActiveIndexes;
  }

  public registerVessel(vessel: Vessel): void {
    this.vessels.push(vessel);
    if (vessel.enforceActive) {
      this.alwaysActive.push(this.vessels?.findIndex(i => i === vessel));
    }
  }

  public toggleVessel(vessel): void {
    const newIndex = this.$_.indexOf(this.vessels, vessel);
    if (this.alwaysActive.includes(newIndex)) return;
    this.changeVessel(newIndex);
    if (typeof this.value !== "undefined") {
      const activeIndex = this.activeIndexes?.find(i => i === newIndex);
      if (!this.$_.isNil(activeIndex)) {
        this.activeIndexes = this.activeIndexes?.filter(i => i !== newIndex);
      } else {
        this.hasMultiActive
          ? this.activeIndexes.push(newIndex)
          : (this.activeIndexes = [newIndex]);
      }
      this.onInput(this.activeIndexes);
    }
  }

  public changeVessel(newIndex): void {
    if (this.activeIndexesComputed.includes(newIndex)) newIndex = -1;
    this.$emit("change", newIndex);
  }

  public onInput(activeIndexes: number[]) {
    this.$emit(
      "input",
      this.hasMultiActive
        ? activeIndexes
        : !this.$_.isNil(activeIndexes[0])
        ? activeIndexes[0]
        : -1
    );
  }
}
